import moment from 'moment-timezone'

import { Periods, SchedulingMethods } from 'constants/meetings'
import { INVALID_SUPERGRAPH_SCOPE_TYPES, isValidSupergraphScopeType } from 'features/meetings/supergraph/scope_utils'
import { STATUS_MAP } from 'services/search_api/constants'
import { mapScopeTypeToBackendValue } from 'shared/api/meetings/utils'

/**
 * Calendly + Google + Outlook. We set this explicitly: cmd-api treated omitted `kinds` as
 * Calendly-only, unlike the legacy search API default of all sources.
 */
export const DEFAULT_SUPERGRAPH_MEETING_KINDS = Object.freeze(['CALENDLY', 'GOOGLE', 'OUTLOOK'])

/**
 * Maps `schedulingMethods` from meetings filters (same source as meeting display settings) to
 * `Meetings_MeetingsFilter.kinds` for supergraph/cmd-api.
 *
 * Aligns with `publisher.meetings.persistent_settings.show_meetings` persistence:
 * - "Scheduled through Calendly and Google Calendar" → `['calendly','google','outlook']` → all three kinds.
 * - "Scheduled through Calendly only" → `['calendly']` → `['CALENDLY']` only.
 *
 * When sources are not narrowed (empty / `all` / unmapped), default to all three so external
 * (Google/Outlook) meetings are included; omitting `kinds` on cmd-api behaved like Calendly-only.
 */
const buildSupergraphKindsFromUiSchedulingMethods = (schedulingMethods) => {
  if (schedulingMethods == null) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (!Array.isArray(schedulingMethods) || schedulingMethods.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  if (schedulingMethods.includes(SchedulingMethods.ALL)) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  const kinds = []
  if (schedulingMethods.includes(SchedulingMethods.CALENDLY)) kinds.push('CALENDLY')
  if (schedulingMethods.includes(SchedulingMethods.GOOGLE)) kinds.push('GOOGLE')
  if (schedulingMethods.includes(SchedulingMethods.OUTLOOK)) kinds.push('OUTLOOK')
  if (kinds.length === 0) {
    return [...DEFAULT_SUPERGRAPH_MEETING_KINDS]
  }
  return kinds
}

export const applyFixedMeetingsOrganizerScope = (filter) => {
  filter.is_organizer = false
  filter.include_notetaker_ineligible_external_events = true
}

export const buildSupergraphTimeFilter = (uiFilters, { today, past, forCount }) => {
  if (!uiFilters) return null
  const { period, startDate, endDate } = uiFilters

  const addDateRangeFilterForCount = (rangeStart, rangeEnd) => ({
    operation: 'BETWEEN',
    start: moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ'),
    end: moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ'),
  })

  if (forCount) {
    switch (period) {
      case Periods.UPCOMING:
        return null
      case Periods.FIXED:
        if (!startDate || !endDate) return null
        return addDateRangeFilterForCount(startDate, endDate)
      case Periods.THIS_WEEK:
        return addDateRangeFilterForCount(moment().startOf('week'), moment().endOf('week'))
      case Periods.THIS_MONTH:
        return addDateRangeFilterForCount(moment().startOf('month'), moment().endOf('month'))
      case Periods.THIS_YEAR:
        return addDateRangeFilterForCount(moment().startOf('year'), moment().endOf('year'))
      case Periods.TODAY:
        return addDateRangeFilterForCount(moment().startOf('day'), moment().endOf('day'))
      default:
        return null
    }
  }

  const todayStart = moment().startOf('day')
  const startOfTodayStr = todayStart.format('YYYY-MM-DDTHH:mm:ssZ')
  const endOfTodayStr = moment().endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')

  const addDateRangeFilter = (rangeStart, rangeEnd) => {
    const formattedStartDate = moment(rangeStart).format('YYYY-MM-DDTHH:mm:ssZ')
    const formattedEndDate = moment(rangeEnd).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ')
    if (past) {
      const actualEndDate = moment(rangeEnd).isBefore(todayStart)
        ? formattedEndDate
        : endOfTodayStr
      return {
        operation: 'BETWEEN',
        start: formattedStartDate,
        end: actualEndDate,
      }
    }
    const actualStartDate = moment(rangeStart).isAfter(todayStart)
      ? formattedStartDate
      : startOfTodayStr
    return {
      operation: 'BETWEEN',
      start: actualStartDate,
      end: formattedEndDate,
    }
  }

  switch (period) {
    case Periods.UPCOMING: {
      if (today) {
        return {
          operation: 'BETWEEN',
          start: startOfTodayStr,
          end: endOfTodayStr,
        }
      }
      if (past) {
        return { operation: 'LT', start: startOfTodayStr }
      }
      return { operation: 'GTE', start: endOfTodayStr }
    }
    case Periods.FIXED:
      if (!startDate || !endDate) return null
      return addDateRangeFilter(startDate, endDate)
    case Periods.THIS_WEEK:
      return addDateRangeFilter(moment().startOf('week'), moment().endOf('week'))
    case Periods.THIS_MONTH:
      return addDateRangeFilter(moment().startOf('month'), moment().endOf('month'))
    case Periods.THIS_YEAR:
      return addDateRangeFilter(moment().startOf('year'), moment().endOf('year'))
    case Periods.TODAY:
      return addDateRangeFilter(moment().startOf('day'), moment().endOf('day'))
    default:
      return null
  }
}

export const buildSupergraphFilterFromUiFilters = (uiFilters, { today, past, forCount } = {}) => {
  if (!uiFilters) return null

  const {
    inviteeEmails,
    eventTypeIds,
    userIds,
    teamIds,
    role,
    ownerType,
    searchKeyword,
    meetingIntelligenceStatus,
    statusIds,
    schedulingMethods,
    scopeType,
    scopeId,
    uuid,
    organizationUuid,
  } = uiFilters

  const filter = {}

  if (organizationUuid != null && organizationUuid !== '') {
    filter.organization_uuid = String(organizationUuid)
  }

  if (inviteeEmails?.length) filter.invitee_emails = inviteeEmails
  if (eventTypeIds?.length) filter.event_type_uuids = eventTypeIds
  if (userIds?.length) filter.user_uuids = userIds
  if (teamIds?.length) filter.team_uuids = teamIds

  if (role) filter.role = role
  if (ownerType) filter.owner_type = ownerType

  if (meetingIntelligenceStatus) {
    filter.meeting_intelligence_status = meetingIntelligenceStatus
  }
  if (searchKeyword) filter.search_keyword = searchKeyword

  const mappedFromRoute = mapScopeTypeToBackendValue(scopeType, scopeId)
  if (mappedFromRoute === 'all_users_and_teams') {
    if (scopeId) filter.scope_id = scopeId
  } else if (mappedFromRoute) {
    if (!INVALID_SUPERGRAPH_SCOPE_TYPES.has(mappedFromRoute)) {
      filter.scope_type = mappedFromRoute
      if (scopeId && mappedFromRoute !== 'my_calendly' && scopeId !== 'me') {
        filter.scope_id = scopeId
      }
    }
  } else if (isValidSupergraphScopeType(scopeType)) {
    filter.scope_type = scopeType
    if (scopeId) filter.scope_id = scopeId
  }

  if (uuid) filter.ids = [uuid]

  if (statusIds?.length) {
    const mapped = statusIds
      .map((id) => STATUS_MAP[id])
      .filter(Boolean)

    if (mapped.length === 1) {
      filter.status = mapped[0]
    } else if (mapped.length > 1) {
      filter.statuses = mapped
    }
  }

  filter.kinds = buildSupergraphKindsFromUiSchedulingMethods(schedulingMethods)

  const timeFilter = buildSupergraphTimeFilter(uiFilters, { today, past, forCount })
  if (timeFilter) filter.start_time = timeFilter

  applyFixedMeetingsOrganizerScope(filter)
  return filter
}

export const buildSupergraphCountInput = (uiFilters) => {
  const filter = buildSupergraphFilterFromUiFilters(uiFilters, { forCount: true })
  return {
    filter,
    pagination: {
      first: 1,
      after: null,
    },
    orderBy: {
      start_time: 'ASC',
    },
  }
}

export const buildSupergraphInput = (uiFilters, { today, past, first, after, order }) => {
  const filter = buildSupergraphFilterFromUiFilters(uiFilters, { today, past })
  return {
    filter,
    pagination: {
      first,
      after: after || null,
    },
    orderBy: {
      start_time: order,
    },
  }
}

export const doesSupergraphLazyArgsMatchFixedVisibleRange = (originalArgs, visibleRangeUiVars) => {
  if (!visibleRangeUiVars || visibleRangeUiVars.period !== Periods.FIXED) return false
  const expected = buildSupergraphFilterFromUiFilters(visibleRangeUiVars, {})
  const lazyPageStartTimeFilter = originalArgs?.filter?.start_time
  const visibleRangeStartTimeFilter = expected?.start_time
  if (!lazyPageStartTimeFilter || !visibleRangeStartTimeFilter) return false
  return lazyPageStartTimeFilter.operation === visibleRangeStartTimeFilter.operation
    && lazyPageStartTimeFilter.start === visibleRangeStartTimeFilter.start
    && lazyPageStartTimeFilter.end === visibleRangeStartTimeFilter.end
}

export const isPreviousPageLazyArgs = (args, isSupergraphEnabled) => {
  if (!args) return false
  if (isSupergraphEnabled) return args.orderBy?.start_time === 'DESC'
  return Boolean(args.isPrevious || args.past)
}
